/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * @flow
 * @format
 */
import React, { Component } from 'react';
import { StyleSheet, Animated } from 'react-native';
const AnimatedView = Animated.View;
export class StickyHeader extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            measured: false,
            layoutY: 0,
            layoutHeight: 0,
            nextHeaderLayoutY: this.props.nextHeaderLayoutY
        };
        this.prevZIndex = 0;
        this.onRef = (ref) => {
            this.containerRef = ref;
        };
        this.onLayout = (event) => {
            const layoutY = event.nativeEvent.layout.y;
            this.setState({
                measured: true,
                layoutY,
                layoutHeight: event.nativeEvent.layout.height
            });
            this.props.onLayout && this.props.onLayout(event);
            const child = this.onlyChild;
            if (child.props.onLayout) {
                child.props.onLayout(event);
            }
        };
        /**
         * 监听滚动，并设置层级
         * @param param
         */
        this.onScrollAnimate = (param) => {
            const { layoutY } = this.state;
            const { value } = param;
            // 不需要调整，则直接退出
            if (!this.needAdjustZIndex) {
                return;
            }
            if (value >= layoutY) {
                // 吸顶时，设置高层级
                this.updateZIndex(10);
            }
            else {
                // 没有吸顶时，设置低层级
                this.updateZIndex(0);
            }
        };
        /**
         * 更新层级
         * @param zIndex
         */
        this.updateZIndex = (zIndex) => {
            // 层级相同不再设置
            if (this.prevZIndex === zIndex) {
                return;
            }
            // 设置层级
            this.containerRef &&
                // @ts-ignore
                this.containerRef.setNativeProps({
                    style: { zIndex }
                });
            // 保存层级
            this.prevZIndex = zIndex;
        };
    }
    componentDidMount() {
        this.addListener();
    }
    componentWillUnmount() {
        this.prevZIndex = 0;
        this.removeListener();
    }
    setNextHeaderY(y) {
        this.setState({ nextHeaderLayoutY: y });
    }
    render() {
        const { inverted, scrollViewHeight } = this.props;
        const { measured, layoutHeight, layoutY, nextHeaderLayoutY } = this.state;
        const inputRange = [-1, 0];
        const outputRange = [0, 0];
        if (measured) {
            if (inverted) {
                // The interpolation looks like:
                // - Negative scroll: no translation
                // - `stickStartPoint` is the point at which the header will start sticking.
                //   It is calculated using the ScrollView viewport height so it is a the bottom.
                // - Headers that are in the initial viewport will never stick, `stickStartPoint`
                //   will be negative.
                // - From 0 to `stickStartPoint` no translation. This will cause the header
                //   to scroll normally until it reaches the top of the scroll view.
                // - From `stickStartPoint` to when the next header y hits the bottom edge of the header: translate
                //   equally to scroll. This will cause the header to stay at the top of the scroll view.
                // - Past the collision with the next header y: no more translation. This will cause the
                //   header to continue scrolling up and make room for the next sticky header.
                //   In the case that there is no next header just translate equally to
                //   scroll indefinitely.
                // eslint-disable-next-line no-eq-null,eqeqeq
                if (scrollViewHeight != null) {
                    const stickStartPoint = layoutY + layoutHeight - scrollViewHeight;
                    if (stickStartPoint > 0) {
                        inputRange.push(stickStartPoint);
                        outputRange.push(0);
                        inputRange.push(stickStartPoint + 1);
                        outputRange.push(1);
                        // If the next sticky header has not loaded yet (probably windowing) or is the last
                        // we can just keep it sticked forever.
                        const collisionPoint = (nextHeaderLayoutY || 0) -
                            layoutHeight -
                            scrollViewHeight;
                        if (collisionPoint > stickStartPoint) {
                            inputRange.push(collisionPoint, collisionPoint + 1);
                            outputRange.push(collisionPoint - stickStartPoint, collisionPoint - stickStartPoint);
                        }
                    }
                }
            }
            else {
                // The interpolation looks like:
                // - Negative scroll: no translation
                // - From 0 to the y of the header: no translation. This will cause the header
                //   to scroll normally until it reaches the top of the scroll view.
                // - From header y to when the next header y hits the bottom edge of the header: translate
                //   equally to scroll. This will cause the header to stay at the top of the scroll view.
                // - Past the collision with the next header y: no more translation. This will cause the
                //   header to continue scrolling up and make room for the next sticky header.
                //   In the case that there is no next header just translate equally to
                //   scroll indefinitely.
                inputRange.push(layoutY);
                outputRange.push(0);
                // If the next sticky header has not loaded yet (probably windowing) or is the last
                // we can just keep it sticked forever.
                const collisionPoint = (nextHeaderLayoutY || 0) - layoutHeight;
                if (collisionPoint >= layoutY) {
                    inputRange.push(collisionPoint, collisionPoint + 1);
                    outputRange.push(collisionPoint - layoutY, collisionPoint - layoutY);
                }
                else {
                    inputRange.push(layoutY + 1);
                    outputRange.push(1);
                }
            }
        }
        // @ts-ignore
        const translateY = this.props.scrollAnimatedValue.interpolate({
            inputRange,
            outputRange
        });
        const child = this.onlyChild;
        return (<AnimatedView collapsable={false} onLayout={this.onLayout} 
        // @ts-ignore
        ref={this.onRef} style={[
            child.props.style,
            styles.header,
            {
                transform: [{ translateY }],
                zIndex: this.needAdjustZIndex ? 0 : 10
            }
        ]}>
                {React.cloneElement(child, {
            style: styles.fill,
            onLayout: undefined // we call this manually through our this.onLayout
        })}
            </AnimatedView>);
    }
    /**
     * 添加监听
     * @private
     */
    addListener() {
        if (!this.needAdjustZIndex) {
            this.removeListener();
            return;
        }
        // @ts-ignore
        this.listenerID = this.props.scrollAnimatedValue.addListener(this.onScrollAnimate);
    }
    /**
     *移除监听
     * @private
     */
    removeListener() {
        if (this.listenerID !== undefined) {
            // @ts-ignore
            this.props.scrollAnimatedValue.removeListener(this.listenerID);
            this.listenerID = undefined;
        }
    }
    /**
     * 是否需要调整层级
     * @private
     */
    get needAdjustZIndex() {
        const { key = '' } = this.onlyChild || { key: '' };
        return key.endsWith('_true');
    }
    get onlyChild() {
        return React.Children.only(this.props.children);
    }
}
// @ts-ignore
const styles = StyleSheet.create({
    header: {
        zIndex: 0
    },
    fill: {
        flex: 1
    }
});
